/**
 * Cheap, client-only "is someone signed in?" hint.
 *
 * Public marketing pages only use this to swap a nav label between
 * "Sign in" and "Open app". Loading the whole backend client (~50 KiB of
 * auth + realtime + storage JS) for that costs a chunk on first paint, so we
 * read the persisted session marker straight out of storage instead and let
 * the real client confirm later, on idle, if it happens to be needed.
 */
function readMarker(store: Storage): boolean {
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (!key || !/^sb-.*-auth-token$/.test(key)) continue;
    const raw = store.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as { expires_at?: number; access_token?: string };
      if (!parsed?.access_token) continue;
      if (typeof parsed.expires_at === "number" && parsed.expires_at * 1000 < Date.now()) continue;
      return true;
    } catch {
      // Non-JSON marker — treat its presence as "probably signed in".
      return true;
    }
  }
  return false;
}

export function hasStoredSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (readMarker(window.localStorage)) return true;
  } catch {
    /* storage blocked */
  }
  try {
    return readMarker(window.sessionStorage);
  } catch {
    return false;
  }
}

function anyMarker(store: Storage): boolean {
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && /^sb-.*-auth-token$/.test(key)) return true;
  }
  return false;
}

/**
 * Lenient version of {@link hasStoredSession}: true whenever a session *might*
 * exist, so we never skip attaching an Authorization header.
 *
 * Unlike the strict hint it ignores `expires_at` (an expired access token is
 * still refreshable via its refresh token) and assumes a session may exist when
 * the app runs framed on a Lovable preview host, where the session is brokered
 * from the editor over postMessage and may not be in local storage at all.
 */
export function maybeHasSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.parent && window.parent !== window) {
      const host = location.hostname;
      const zones = [
        "lovableproject.com",
        "lovableproject-dev.com",
        "lovable.app",
        "gpt-eng.com",
        "gptengineer.run",
      ];
      if (zones.some((z) => host === z || host.endsWith("." + z))) return true;
    }
  } catch {
    /* cross-origin parent access blocked — fall through */
  }
  try {
    if (anyMarker(window.localStorage)) return true;
  } catch {
    /* storage blocked */
  }
  try {
    return anyMarker(window.sessionStorage);
  } catch {
    return false;
  }
}
